import type { Courier } from './enums.js';

const IN = 2.54;
const OZ = 0.028349523125;
/**
 * Parcel presets offered when buying a label (stored in cm / kg; the screens show inches / ounces).
 * Foreign Founders mostly ships bank letters and cards. `defaultParcel` in the Shippo integration picks the default.
 */
export const LABEL_PARCEL_PRESETS = [
  { key: 'envelope', name: 'Envelope (letter / documents)', lengthCm: +(12.5 * IN).toFixed(2), widthCm: +(9.5 * IN).toFixed(2), heightCm: +(0.25 * IN).toFixed(2), weightKg: +(3 * OZ).toFixed(4) },
  { key: 'card_mailer', name: 'Card mailer (padded envelope)', lengthCm: +(9 * IN).toFixed(2), widthCm: +(6 * IN).toFixed(2), heightCm: +(0.5 * IN).toFixed(2), weightKg: +(2 * OZ).toFixed(4) },
  { key: 'small_box', name: 'Small box', lengthCm: +(8.69 * IN).toFixed(2), widthCm: +(5.44 * IN).toFixed(2), heightCm: +(1.75 * IN).toFixed(2), weightKg: +(8 * OZ).toFixed(4) },
  { key: 'medium_box', name: 'Medium box', lengthCm: +(11 * IN).toFixed(2), widthCm: +(8.5 * IN).toFixed(2), heightCm: +(5.5 * IN).toFixed(2), weightKg: +(32 * OZ).toFixed(4) },
] as const;
export type LabelParcelPresetKey = (typeof LABEL_PARCEL_PRESETS)[number]['key'];

export function normalizeTrackingNumber(raw: string): string {
  return raw.replace(/[\s-]/g, '').toUpperCase();
}

/** Loose sanity check applied to every tracking number regardless of courier. */
export function isPlausibleTrackingNumber(raw: string): boolean {
  return /^[A-Z0-9]{8,40}$/.test(normalizeTrackingNumber(raw));
}

const PATTERNS: { courier: Courier; re: RegExp }[] = [
  { courier: 'ups', re: /^1Z[0-9A-Z]{16}$/ },
  { courier: 'ups', re: /^(T\d{10}|\d{9}|\d{26})$/ },
  { courier: 'usps', re: /^(94|93|92|95|91|420\d{5}(94|93|92|95|91))\d{18,20}$/ },
  { courier: 'usps', re: /^9\d{19,21}$/ },
  { courier: 'usps', re: /^[A-Z]{2}\d{9}US$/ },
  { courier: 'fedex', re: /^\d{12}$/ },
  { courier: 'fedex', re: /^\d{15}$/ },
  { courier: 'fedex', re: /^(96\d{20}|\d{20})$/ },
  { courier: 'dhl', re: /^\d{10}$/ },
  { courier: 'dhl', re: /^(JJD\d{18,20}|JVGL\d{16,20}|GM\d{16,20}|LX\d{9}DE|[A-Z]{3}\d{7})$/ },
];

/**
 * Couriers whose published formats match. Several couriers share numeric formats, so this is a hint only —
 * the employee confirms the courier (Shippo needs it to track the number).
 */
export function detectCourierCandidates(raw: string): Courier[] {
  const n = normalizeTrackingNumber(raw);
  const found = new Set<Courier>();
  for (const p of PATTERNS) if (p.re.test(n)) found.add(p.courier);
  return [...found];
}

/** The courier when the number's format points to exactly one; otherwise null (the employee selects it). */
export function detectCourier(raw: string): Courier | null {
  const c = detectCourierCandidates(raw);
  return c.length === 1 ? c[0] : null;
}

export function validateTrackingForCourier(raw: string, courier: Courier): { ok: boolean; warning?: string } {
  if (!isPlausibleTrackingNumber(raw)) return { ok: false, warning: 'Tracking numbers must be 8–40 letters or digits.' };
  if (courier === 'other') return { ok: true };
  const candidates = detectCourierCandidates(raw);
  if (candidates.length === 0) return { ok: true, warning: `Format not recognised for ${courier.toUpperCase()}; please double-check.` };
  if (!candidates.includes(courier))
    return { ok: true, warning: `Format looks like ${candidates.map((c) => c.toUpperCase()).join(' / ')}, not ${courier.toUpperCase()}.` };
  return { ok: true };
}

/** Age bands used for "tracking freshness" warnings. */
export type Freshness = 'fresh' | 'aging' | 'stale' | 'never';

export function trackingFreshness(lastCourierEventAt: Date | string | null | undefined, now = new Date()): Freshness {
  if (!lastCourierEventAt) return 'never';
  const ageH = (now.getTime() - new Date(lastCourierEventAt).getTime()) / 3_600_000;
  if (ageH <= 24) return 'fresh';
  if (ageH <= 72) return 'aging';
  return 'stale';
}
